import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Bug, Upload, RefreshCw, Loader2, ExternalLink, Image, 
  CheckCircle2, Clock, AlertCircle, Play, Filter, Download,
  X, Eye, ChevronDown, ChevronUp, FileSpreadsheet
} from 'lucide-react';

const STATUS_CONFIG = {
  'Not Started': { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: Clock },
  'In Progress': { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Play },
  'Done': { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
};

export default function AdminBugFixingPage() {
  const { api } = useAuth();
  const [sheetUrl, setSheetUrl] = useState('');
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedBug, setSelectedBug] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [localStatuses, setLocalStatuses] = useState({}); // Track local status changes
  const [lastImportTime, setLastImportTime] = useState(null);
  const [fixBugsDialogOpen, setFixBugsDialogOpen] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  // Load saved data from localStorage
  useEffect(() => {
    const savedUrl = localStorage.getItem('bugTrackerSheetUrl');
    const savedBugs = localStorage.getItem('bugTrackerBugs');
    const savedStatuses = localStorage.getItem('bugTrackerStatuses');
    const savedImportTime = localStorage.getItem('bugTrackerLastImport');
    
    if (savedUrl) setSheetUrl(savedUrl);
    if (savedBugs) setBugs(JSON.parse(savedBugs));
    if (savedStatuses) setLocalStatuses(JSON.parse(savedStatuses));
    if (savedImportTime) setLastImportTime(savedImportTime);
  }, []);

  // Save to localStorage when data changes
  useEffect(() => {
    if (bugs.length > 0) {
      localStorage.setItem('bugTrackerBugs', JSON.stringify(bugs));
    }
  }, [bugs]);

  useEffect(() => {
    if (Object.keys(localStatuses).length > 0) {
      localStorage.setItem('bugTrackerStatuses', JSON.stringify(localStatuses));
    }
  }, [localStatuses]);

  const importFromSheet = async () => {
    if (!sheetUrl) {
      toast.error('Please enter a Google Sheet URL');
      return;
    }

    setImporting(true);
    try {
      // Use the existing sheets preview endpoint (no trailing slash)
      const response = await api.post('/sheets/preview', { url: sheetUrl });
      
      if (response.data.success) {
        const { columns, preview } = response.data;
        
        // Validate expected columns
        const requiredColumns = ['Bug Type', 'Bug Name', 'Bug Description', 'Status'];
        const hasRequiredColumns = requiredColumns.every(col => 
          columns.some(c => c.toLowerCase().includes(col.toLowerCase().replace(' ', '')))
        );

        // Map the data to our bug format
        const mappedBugs = preview.map((row, index) => {
          // Find the column names (case-insensitive matching)
          const findColumn = (searchTerm) => {
            const key = columns.find(c => 
              c.toLowerCase().replace(/\s+/g, '').includes(searchTerm.toLowerCase().replace(/\s+/g, ''))
            );
            return key ? row[key] : '';
          };

          return {
            id: `bug-${index}-${Date.now()}`,
            bugType: findColumn('bugtype') || findColumn('type') || 'General',
            bugName: findColumn('bugname') || findColumn('name') || `Bug ${index + 1}`,
            bugDescription: findColumn('bugdescription') || findColumn('description') || '',
            screenshot: findColumn('screenshot') || findColumn('image') || '',
            status: findColumn('status') || 'Not Started',
            originalIndex: index,
          };
        });

        setBugs(mappedBugs);
        localStorage.setItem('bugTrackerSheetUrl', sheetUrl);
        setLastImportTime(new Date().toISOString());
        localStorage.setItem('bugTrackerLastImport', new Date().toISOString());
        
        toast.success(`Imported ${mappedBugs.length} bugs from spreadsheet`);
      }
    } catch (error) {
      console.error('Import error:', error);
      const message = error.response?.data?.detail || error.response?.data?.help || 'Failed to import from Google Sheet. Make sure the sheet is published to web.';
      toast.error(message);
    } finally {
      setImporting(false);
    }
  };

  const updateBugStatus = (bugId, newStatus) => {
    setLocalStatuses(prev => ({
      ...prev,
      [bugId]: newStatus
    }));
    
    // Also update the bugs array for immediate UI feedback
    setBugs(prev => prev.map(bug => 
      bug.id === bugId ? { ...bug, status: newStatus } : bug
    ));
    
    toast.success(`Bug status updated to "${newStatus}"`);
  };

  const getBugStatus = (bug) => {
    return localStatuses[bug.id] || bug.status;
  };

  const getFilteredBugs = () => {
    return bugs.filter(bug => {
      const status = getBugStatus(bug);
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (typeFilter !== 'all' && bug.bugType !== typeFilter) return false;
      return true;
    });
  };

  const getBugTypes = () => {
    const types = [...new Set(bugs.map(b => b.bugType).filter(Boolean))];
    return types;
  };

  const exportToCSV = () => {
    const headers = ['Bug Type', 'Bug Name', 'Bug Description', 'Screenshot', 'Status'];
    const rows = bugs.map(bug => [
      bug.bugType,
      bug.bugName,
      bug.bugDescription,
      bug.screenshot,
      getBugStatus(bug)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bug_tracker_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Bug list exported to CSV');
  };

  const markAllFilteredAsDone = () => {
    const filtered = getFilteredBugs();
    const newStatuses = { ...localStatuses };
    filtered.forEach(bug => {
      newStatuses[bug.id] = 'Done';
    });
    setLocalStatuses(newStatuses);
    setBugs(prev => prev.map(bug => {
      if (filtered.some(f => f.id === bug.id)) {
        return { ...bug, status: 'Done' };
      }
      return bug;
    }));
    toast.success(`Marked ${filtered.length} bugs as Done`);
  };

  const stats = {
    total: bugs.length,
    notStarted: bugs.filter(b => getBugStatus(b) === 'Not Started').length,
    inProgress: bugs.filter(b => getBugStatus(b) === 'In Progress').length,
    done: bugs.filter(b => getBugStatus(b) === 'Done').length,
  };

  const filteredBugs = getFilteredBugs();

  // Generate bug report for AI fixing
  const generateBugReport = () => {
    const bugsToFix = bugs.filter(b => getBugStatus(b) !== 'Done');
    
    if (bugsToFix.length === 0) {
      toast.info('All bugs are already marked as Done!');
      return '';
    }

    let report = `## Bug Fixing Request\n\n`;
    report += `I have ${bugsToFix.length} bugs that need to be fixed. Please work through them systematically:\n\n`;
    
    bugsToFix.forEach((bug, index) => {
      report += `### Bug ${index + 1}: ${bug.bugName}\n`;
      report += `- **Type:** ${bug.bugType}\n`;
      report += `- **Status:** ${getBugStatus(bug)}\n`;
      report += `- **Description:** ${bug.bugDescription || 'No description provided'}\n`;
      if (bug.screenshot) {
        report += `- **Screenshot:** ${bug.screenshot}\n`;
      }
      report += `\n`;
    });

    report += `---\n\n`;
    report += `Please fix these bugs one by one (or in bulk where related), test each fix, and let me know when complete so I can update the status to "Done".\n`;
    
    return report;
  };

  const copyBugReportToClipboard = async () => {
    const report = generateBugReport();
    if (!report) return;
    
    try {
      await navigator.clipboard.writeText(report);
      setCopiedToClipboard(true);
      toast.success('Bug report copied to clipboard! Paste it in the chat to start fixing.');
      setTimeout(() => setCopiedToClipboard(false), 3000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const getBugsToFix = () => bugs.filter(b => getBugStatus(b) !== 'Done');

  return (
    <div className="space-y-6" data-testid="bug-fixing-page">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Bug className="h-6 w-6 text-red-500" />
          Bug Fixing Tracker
        </h1>
        <p className="text-muted-foreground">Import bugs from Google Sheets and track their resolution status</p>
      </div>

      {/* Import Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Import from Google Sheets
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Your Google Sheet should have columns: <strong>Bug Type, Bug Name, Bug Description, Screenshot, Status</strong>
              <br />
              <span className="text-sm">Make sure to publish the sheet: File → Share → Publish to web → CSV format</span>
            </AlertDescription>
          </Alert>
          
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Paste your Google Sheets URL here..."
                value={sheetUrl}
                onChange={(e) => setSheetUrl(e.target.value)}
                data-testid="sheet-url-input"
              />
            </div>
            <Button onClick={importFromSheet} disabled={importing}>
              {importing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Import Bugs
            </Button>
            {bugs.length > 0 && (
              <Button variant="outline" onClick={importFromSheet} disabled={importing}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            )}
          </div>
          
          {lastImportTime && (
            <p className="text-xs text-muted-foreground">
              Last imported: {new Date(lastImportTime).toLocaleString()}
            </p>
          )}
        </CardContent>
      </Card>

      {bugs.length > 0 && (
        <>
          {/* Stats Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Bug className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="text-2xl font-bold">{stats.total}</p>
                    <p className="text-sm text-muted-foreground">Total Bugs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Clock className="h-8 w-8 text-gray-600" />
                  <div>
                    <p className="text-2xl font-bold">{stats.notStarted}</p>
                    <p className="text-sm text-muted-foreground">Not Started</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Play className="h-8 w-8 text-yellow-600" />
                  <div>
                    <p className="text-2xl font-bold">{stats.inProgress}</p>
                    <p className="text-sm text-muted-foreground">In Progress</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="text-2xl font-bold">{stats.done}</p>
                    <p className="text-sm text-muted-foreground">Done</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fix Bugs Action Card */}
          {getBugsToFix().length > 0 && (
            <Card className="border-red-200 bg-gradient-to-r from-red-50 to-orange-50">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-red-100 flex items-center justify-center">
                      <Bug className="h-7 w-7 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">Ready to Fix Bugs?</h3>
                      <p className="text-muted-foreground">
                        {getBugsToFix().length} bug{getBugsToFix().length !== 1 ? 's' : ''} waiting to be fixed
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="lg" 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => setFixBugsDialogOpen(true)}
                    data-testid="fix-bugs-btn"
                  >
                    <Bug className="h-5 w-5 mr-2" />
                    Fix Bugs with AI
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filters and Actions */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                <div className="flex flex-wrap gap-4">
                  {/* Status Filter */}
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Status:</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="Not Started">Not Started</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Type Filter */}
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Type:</Label>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {getBugTypes().map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={exportToCSV}>
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                  {filteredBugs.length > 0 && statusFilter !== 'Done' && (
                    <Button size="sm" onClick={markAllFilteredAsDone} className="bg-green-600 hover:bg-green-700">
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Mark All as Done
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bugs Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                Bug List ({filteredBugs.length} of {bugs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Bug Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-32">Status</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBugs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No bugs found matching your filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBugs.map((bug, index) => {
                      const status = getBugStatus(bug);
                      const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG['Not Started'];
                      const StatusIcon = statusConfig.icon;

                      return (
                        <TableRow key={bug.id} data-testid={`bug-row-${index}`}>
                          <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{bug.bugType}</Badge>
                          </TableCell>
                          <TableCell className="font-medium">{bug.bugName}</TableCell>
                          <TableCell className="max-w-xs">
                            <p className="truncate text-sm text-muted-foreground">
                              {bug.bugDescription || 'No description'}
                            </p>
                          </TableCell>
                          <TableCell>
                            <Select 
                              value={status} 
                              onValueChange={(value) => updateBugStatus(bug.id, value)}
                            >
                              <SelectTrigger className={`w-full ${statusConfig.color} border`}>
                                <SelectValue>
                                  <div className="flex items-center gap-1">
                                    <StatusIcon className="h-3 w-3" />
                                    <span>{status}</span>
                                  </div>
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Not Started">
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    Not Started
                                  </div>
                                </SelectItem>
                                <SelectItem value="In Progress">
                                  <div className="flex items-center gap-2">
                                    <Play className="h-4 w-4" />
                                    In Progress
                                  </div>
                                </SelectItem>
                                <SelectItem value="Done">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4" />
                                    Done
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => { setSelectedBug(bug); setDetailsOpen(true); }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Bug Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-red-500" />
              Bug Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedBug && (
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="text-base px-3 py-1">
                  {selectedBug.bugType}
                </Badge>
                <Select 
                  value={getBugStatus(selectedBug)} 
                  onValueChange={(value) => updateBugStatus(selectedBug.id, value)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not Started">Not Started</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Done">Done</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Bug Name */}
              <div>
                <Label className="text-sm text-muted-foreground">Bug Name</Label>
                <h3 className="text-xl font-semibold">{selectedBug.bugName}</h3>
              </div>

              {/* Description */}
              <div>
                <Label className="text-sm text-muted-foreground">Description</Label>
                <div className="mt-1 p-4 bg-muted/50 rounded-lg whitespace-pre-wrap">
                  {selectedBug.bugDescription || 'No description provided'}
                </div>
              </div>

              {/* Screenshot */}
              {selectedBug.screenshot && (
                <div>
                  <Label className="text-sm text-muted-foreground">Screenshot</Label>
                  <div className="mt-2">
                    {selectedBug.screenshot.startsWith('http') ? (
                      <div className="space-y-2">
                        <a 
                          href={selectedBug.screenshot} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Open Screenshot
                        </a>
                        <img 
                          src={selectedBug.screenshot} 
                          alt="Bug screenshot" 
                          className="max-w-full rounded-lg border"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">{selectedBug.screenshot}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setDetailsOpen(false)}
                >
                  Close
                </Button>
                {getBugStatus(selectedBug) !== 'Done' && (
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      updateBugStatus(selectedBug.id, 'Done');
                      setDetailsOpen(false);
                    }}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark as Done
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fix Bugs Dialog */}
      <Dialog open={fixBugsDialogOpen} onOpenChange={setFixBugsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-red-500" />
              Fix Bugs with AI Assistant
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-700">
                Click the button below to copy the bug report. Then paste it in the chat to have me fix all bugs systematically.
              </AlertDescription>
            </Alert>

            {/* Bug Summary */}
            <div className="p-4 bg-muted/50 rounded-lg">
              <h4 className="font-medium mb-2">Bugs to Fix ({getBugsToFix().length})</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {getBugsToFix().map((bug, index) => (
                  <div key={bug.id} className="flex items-start gap-2 text-sm p-2 bg-white rounded border">
                    <span className="font-medium text-muted-foreground">{index + 1}.</span>
                    <div className="flex-1">
                      <span className="font-medium">{bug.bugName}</span>
                      <Badge variant="outline" className="ml-2 text-xs">{bug.bugType}</Badge>
                      {bug.bugDescription && (
                        <p className="text-muted-foreground text-xs mt-1 line-clamp-2">{bug.bugDescription}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Preview of generated report */}
            <div>
              <Label className="text-sm text-muted-foreground">Generated Bug Report Preview</Label>
              <Textarea 
                className="mt-2 h-48 font-mono text-xs"
                value={generateBugReport()}
                readOnly
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setFixBugsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={() => {
                  copyBugReportToClipboard();
                  // Mark all as In Progress
                  const bugsToFix = getBugsToFix();
                  bugsToFix.forEach(bug => {
                    if (getBugStatus(bug) === 'Not Started') {
                      updateBugStatus(bug.id, 'In Progress');
                    }
                  });
                }}
                data-testid="copy-bug-report-btn"
              >
                {copiedToClipboard ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Copied! Now paste in chat
                  </>
                ) : (
                  <>
                    <Bug className="h-4 w-4 mr-2" />
                    Copy Bug Report & Start Fixing
                  </>
                )}
              </Button>
            </div>

            {copiedToClipboard && (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700">
                  Bug report copied! Close this dialog and paste it in the chat. I'll work through each bug and fix them for you.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Empty State */}
      {bugs.length === 0 && !importing && (
        <Card>
          <CardContent className="py-16 text-center">
            <Bug className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No bugs imported yet</h2>
            <p className="text-muted-foreground mb-4 max-w-md mx-auto">
              Create a Google Sheet with columns: Bug Type, Bug Name, Bug Description, Screenshot, Status.
              Publish it to web and paste the URL above to import.
            </p>
            <div className="bg-muted/50 rounded-lg p-4 max-w-lg mx-auto text-left">
              <p className="font-medium mb-2">Expected columns:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• <strong>Bug Type</strong> - Category (UI, Backend, API, etc.)</li>
                <li>• <strong>Bug Name</strong> - Short title for the bug</li>
                <li>• <strong>Bug Description</strong> - Detailed description</li>
                <li>• <strong>Screenshot</strong> - URL to screenshot image</li>
                <li>• <strong>Status</strong> - Not Started, In Progress, or Done</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
